import React, { useEffect, useState } from 'react';
import { Alert, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { reissueProvisionedTenantInvitation } from '../../sitePublishing/api';
import { TenantPublishStatus } from '../../sitePublishing/types';
import { createControlPlaneTenantUrl } from '../../tenancy/tenantUrls';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Who the business's owner invitation goes to, until the owner accepts it.
// Businesses built from a site brief can start with no owner (while you're still
// selling to them); enter the owner here and send the invitation when ready.
const OwnerInvitationCard: React.FC<{ siteId: string; invitation: TenantPublishStatus['invitation'] }> = ({ siteId, invitation }) => {
  const queryClient = useQueryClient();
  const [ownerName, setOwnerName] = useState(invitation?.ownerName ?? '');
  const [ownerEmail, setOwnerEmail] = useState(invitation?.ownerEmail ?? '');
  useEffect(() => {
    setOwnerName(invitation?.ownerName ?? '');
    setOwnerEmail(invitation?.ownerEmail ?? '');
  }, [invitation?.ownerName, invitation?.ownerEmail]);
  const sendMutation = useMutation({
    mutationFn: () => reissueProvisionedTenantInvitation(siteId, { ownerName: ownerName.trim(), ownerEmail: ownerEmail.trim() }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant-publish-status', siteId] }),
  });
  const hasOwner = Boolean(invitation?.ownerEmail);
  const valid = Boolean(ownerName.trim()) && EMAIL_PATTERN.test(ownerEmail.trim());
  const invitationUrl = invitation && hasOwner ?
    createControlPlaneTenantUrl('/signup', siteId, { code: invitation.accessCode }) : '';

  return <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
    <Typography variant="h6">Owner invitation</Typography>
    <Typography color="text.secondary" sx={{ mb: 2 }}>
      {hasOwner && invitation ?
        `Sent to ${invitation.ownerEmail} · expires ${new Date(invitation.expiresAt).toLocaleString()}` :
        'No owner yet. Add who should run this business, then send their invitation.'}
    </Typography>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
      <TextField size="small" label="Owner name" value={ownerName} onChange={(event) => setOwnerName(event.target.value)} fullWidth />
      <TextField size="small" type="email" label="Owner email" value={ownerEmail}
        onChange={(event) => setOwnerEmail(event.target.value)} fullWidth />
    </Stack>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      <Button variant="contained" disabled={!valid || sendMutation.isPending} onClick={() => sendMutation.mutate()}>
        {sendMutation.isPending ? 'Sending…' : hasOwner ? 'Send a new invitation' : 'Send invitation'}
      </Button>
      {invitationUrl ? <Button variant="outlined" onClick={() => void navigator.clipboard.writeText(invitationUrl)}>
        Copy invitation link
      </Button> : null}
    </Stack>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
      Each send emails a fresh link that works for 7 days and replaces the previous one. The owner can sign up with any email.
    </Typography>
    {sendMutation.isSuccess ? <Alert severity="success" sx={{ mt: 1.5 }}>Invitation sent to {sendMutation.data.ownerEmail}.</Alert> : null}
    {sendMutation.isError ? <Alert severity="error" sx={{ mt: 1.5 }}>{sendMutation.error.message}</Alert> : null}
  </Paper>;
};

export default OwnerInvitationCard;
